import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /**
   * In-flight fetches keyed by page number. Concurrent waiters share one request,
   * so a fast-scrolling user can't lose a fetch by clobbering a single resolver.
   * @type {Map<number, Promise<void>>}
   */
  #inFlightFetches = new Map();

  /**
   * Pages already inserted into the grid. Prevents duplicate appends when two
   * intersection callbacks race past the same boundary.
   * @type {Set<number>}
   */
  #renderedPages = new Set();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {string | null} */
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();
    this.#trackInitialRenderedPages();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
  }

  #trackInitialRenderedPages() {
    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;
    for (const card of cards) {
      const page = Number(card.dataset.page);
      if (Number.isInteger(page)) this.#renderedPages.add(page);
    }
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else {
              this.#renderNextPage();
            }
          }
        }
      },
      {
        rootMargin: '100px',
      }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page > this.#getLastPage() || pageInfo.page < 1) return false;

    return true;
  }

  #getLastPage() {
    const { grid } = this.refs;

    if (!grid) return 0;

    return Number(grid.getAttribute('last-page'));
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {Promise<void> | undefined}
   */
  #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;
    return this.#fetchPageByNumber(page.page, page.url);
  }

  /**
   * Fetch a specific page. Concurrent callers for the same page share one request.
   * Failures are surfaced via the returned promise — the caller can retry.
   *
   * @param {number} pageNumber
   * @param {URL} url
   * @param {boolean} [bypassCache] - When true, skip the section-renderer cache so a
   *   previously-cached error response is re-fetched from origin.
   * @returns {Promise<void>}
   */
  #fetchPageByNumber(pageNumber, url, bypassCache = false) {
    if (this.pages.has(pageNumber) && !bypassCache) return Promise.resolve();

    const existing = this.#inFlightFetches.get(pageNumber);
    if (existing) return existing;

    const fetchPromise = (async () => {
      try {
        const html = await sectionRenderer.getSectionHTML(this.sectionId, !bypassCache, url);
        if (!this.#htmlHasGrid(html)) {
          throw new Error(`Page ${pageNumber} response is missing the product grid`);
        }
        this.pages.set(pageNumber, html);
      } finally {
        this.#inFlightFetches.delete(pageNumber);
      }
    })();

    this.#inFlightFetches.set(pageNumber, fetchPromise);
    return fetchPromise;
  }

  /**
   * Returns the parsed grid items for a page, fetching it if needed.
   * Retries once with a cache bypass if the first attempt fails or returns invalid HTML.
   *
   * @param {number} pageNumber
   * @param {URL} url
   * @returns {Promise<NodeListOf<Element> | undefined>}
   */
  async #getOrFetchGridForPage(pageNumber, url) {
    const cached = this.#getGridForPage(pageNumber);
    if (cached) return cached;

    try {
      await this.#fetchPageByNumber(pageNumber, url);
    } catch (error) {
      console.error(`PaginatedList: initial fetch for page ${pageNumber} failed, retrying`, error);
      try {
        await this.#fetchPageByNumber(pageNumber, url, true);
      } catch (retryError) {
        console.error(`PaginatedList: retry for page ${pageNumber} failed`, retryError);
        return;
      }
    }

    return this.#getGridForPage(pageNumber);
  }

  /**
   * @param {string} html
   * @returns {boolean}
   */
  #htmlHasGrid(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector('[ref="grid"]') !== null;
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#renderedPages.has(nextPage.page)) return;

    const nextPageItemElements = await this.#getOrFetchGridForPage(nextPage.page, nextPage.url);
    if (!nextPageItemElements) return;

    // Re-check after the await: a concurrent caller may have already appended this page.
    if (this.#renderedPages.has(nextPage.page)) return;
    this.#renderedPages.add(nextPage.page);

    grid.append(...nextPageItemElements);

    this.#processNewElements();

    history.pushState('', '', nextPage.url.toString());

    setTimeout(() => this.#fetchPage('next'), 0);
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    if (this.#renderedPages.has(previousPage.page)) return;

    const previousPageItemElements = await this.#getOrFetchGridForPage(previousPage.page, previousPage.url);
    if (!previousPageItemElements) return;

    // Re-check after the await: a concurrent caller may have already prepended this page.
    if (this.#renderedPages.has(previousPage.page)) return;
    this.#renderedPages.add(previousPage.page);

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    setTimeout(() => this.#fetchPage('previous'), 0);
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }
}
